import { useRef, useState } from 'react';
import clsx from 'clsx';

import { useOutsideClick } from '@/hooks/ui/useOutsideClick';
import { FloatingPanel } from '@/shared/ui/FloatingPanel';
import { onAltArrowDownOpen } from '../../utils';
import { RPriorityArrow } from './icons';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ANY = any;

const VALUES = Array.from({ length: 11 }, (_, n) => n);

/** «Приоритет: N / 10» из макета: подчёркнутое значение + сетка 0…10. */
export function RPrioritySelect({
  value,
  onChange,
  disabled,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useOutsideClick({
    enabled: open,
    refs: [btnRef as ANY, panelRef as ANY],
    onOutside: () => setOpen(false),
  });

  return (
    <span className="flex items-center gap-[7px] whitespace-nowrap">
      <RPriorityArrow />
      <span className="text-[11px] font-medium text-[rgba(20,30,49,0.6)] min-[620px]:text-[12px]">
        Приоритет:
      </span>
      <span className="relative flex items-baseline gap-[4px]">
        <button
          ref={btnRef}
          type="button"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          onKeyDown={onAltArrowDownOpen(open, () => setOpen(true))}
          className={clsx(
            'border-b border-[rgba(59,77,127,0.3)] px-[6px] pb-[3px] pt-[2px]',
            'text-[12px] font-bold text-[#141E31] transition-colors',
            disabled
              ? 'cursor-not-allowed opacity-50'
              : 'hover:border-[#57B792]'
          )}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          {value === null || value === undefined ? '__' : String(value)}
        </button>
        <span className="text-[12px] text-[rgba(20,30,49,0.5)]">/ 10</span>

        {open ? (
          <FloatingPanel
            ref={panelRef}
            anchorRef={btnRef}
            topOffset={30}
            className="flex w-[208px] flex-wrap gap-[4px] rounded-[12px] bg-white p-[8px] shadow-[0_16px_36px_rgba(20,30,49,0.22)]"
            role="menu"
          >
            {VALUES.map((n) => {
              const active = value === n;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => {
                    onChange(n);
                    setOpen(false);
                  }}
                  className={clsx(
                    'h-[28px] flex-[0_0_32px] rounded-[8px] text-[11px] font-bold transition',
                    active
                      ? 'bg-[#3B4D7F] text-white'
                      : 'bg-[rgba(59,77,127,0.06)] text-[#141E31] hover:bg-[rgba(59,77,127,0.12)]'
                  )}
                  role="menuitem"
                >
                  {n}
                </button>
              );
            })}
          </FloatingPanel>
        ) : null}
      </span>
    </span>
  );
}
